import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { toCamelCase } from '../lib/typeHelper'

export enum GeicoQaRunStatus {
  Pending = 'PENDING',
  Quoted = 'QUOTED',
  Declined = 'DECLINED',
  Error = 'ERROR',
}

export interface GeicoQaRun {
  id: string
  fixtureSlug: string
  fixtureName: string
  batchId: string | null
  status: GeicoQaRunStatus
  passed: boolean | null
  failureReason: string
  premiumAnnual: string | null
  premium6Month: string | null
  retentionId: string
  dashboardUrl: string
  quoteUrl: string
  runUrl: string
  integrationRunId: string
  attempt: number
  error: string
  startedAt: string | null
  finishedAt: string | null
  durationSeconds: number | null
  dateCreated: string
}

export interface GeicoQaFixture {
  slug: string
  name: string
  description: string
  latestRun: GeicoQaRun | null
}

interface FixturesResponse {
  fixtures: GeicoQaFixture[]
}

interface StartRunsResponse {
  started: GeicoQaRun[]
  skipped: string[]
}

interface RunsResponse {
  runs: GeicoQaRun[]
}

const FIXTURES_KEY = ['GEICO_QA_FIXTURES']

function getFixtures(): Promise<GeicoQaFixture[]> {
  return axios
    .get('/api/v1/insure/qa/geico-fixtures/')
    .then((response) => (toCamelCase(response.data) as FixturesResponse).fixtures)
}

function startRuns(payload: { slugs?: string[]; runAll?: boolean }): Promise<StartRunsResponse> {
  return axios
    .post('/api/v1/insure/qa/geico-runs/', { slugs: payload.slugs, run_all: payload.runAll ?? false })
    .then((response) => toCamelCase(response.data) as StartRunsResponse)
}

function getRuns(slug: string): Promise<GeicoQaRun[]> {
  return axios
    .get(`/api/v1/insure/qa/geico-runs/${slug}/`)
    .then((response) => (toCamelCase(response.data) as RunsResponse).runs)
}

/** Polls while any fixture is mid-run — a GEICO portal session takes ~20 minutes. */
export function useGeicoQaFixtures() {
  return useQuery({
    queryKey: FIXTURES_KEY,
    queryFn: getFixtures,
    refetchInterval: (query) =>
      query.state.data?.some((fixture) => fixture.latestRun?.status === GeicoQaRunStatus.Pending)
        ? 15_000
        : false,
  })
}

export function useStartGeicoQaRuns() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: startRuns,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FIXTURES_KEY }),
  })
}

export function useGeicoQaRuns(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ['GEICO_QA_RUNS', slug],
    queryFn: () => getRuns(slug),
    enabled,
  })
}
